// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { AskGetabstractAnswerContextDoc, ActionableMiniView, AskGetabstractAnswerStatus, AskGetabstractAnswer } from "../schema";
import { serialize_AskGetabstractAnswerContextDoc_internal, deserialize_AskGetabstractAnswerContextDoc_internal } from "./AskGetabstractAnswerContextDoc";
import { serialize_ActionableMiniView_internal, deserialize_ActionableMiniView_internal } from "./ActionableMiniView";
import { serialize_AskGetabstractAnswerStatus_internal, deserialize_AskGetabstractAnswerStatus_internal } from "./AskGetabstractAnswerStatus";

export function serialize_AskGetabstractAnswer_internal(value: AskGetabstractAnswer): unknown {
  return ((value: {
    answer: string;
    contextDocs: AskGetabstractAnswerContextDoc[];
    detectedLanguageLabel: string | null;
    previousQuestionUuid: string | null;
    question: string;
    questionUuid: string;
    relatedActionables: ActionableMiniView[];
    relatedQuestions: string[];
    status: AskGetabstractAnswerStatus;
  }): unknown => {
    return {
      answer: ((value: string): unknown => {
        return value;
      })(value["answer"]),
      contextDocs: ((value: AskGetabstractAnswerContextDoc[]): unknown => {
        return value.map((value: AskGetabstractAnswerContextDoc): unknown => {
          return serialize_AskGetabstractAnswerContextDoc_internal(value);
        });
      })(value["contextDocs"]),
      detectedLanguageLabel: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["detectedLanguageLabel"]),
      previousQuestionUuid: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["previousQuestionUuid"]),
      question: ((value: string): unknown => {
        return value;
      })(value["question"]),
      questionUuid: ((value: string): unknown => {
        return value;
      })(value["questionUuid"]),
      relatedActionables: ((value: ActionableMiniView[]): unknown => {
        return value.map((value: ActionableMiniView): unknown => {
          return serialize_ActionableMiniView_internal(value);
        });
      })(value["relatedActionables"]),
      relatedQuestions: ((value: string[]): unknown => {
        return value.map((value: string): unknown => {
          return value;
        });
      })(value["relatedQuestions"]),
      status: ((value: AskGetabstractAnswerStatus): unknown => {
        return serialize_AskGetabstractAnswerStatus_internal(value);
      })(value["status"]),
    };
  })(value);
}
export function serialize_AskGetabstractAnswer(obj: AskGetabstractAnswer): string {
  return renderJsonWithNumbersAsBigint(serialize_AskGetabstractAnswer_internal(obj));
}
export function deserialize_AskGetabstractAnswer_internal(value: unknown): ParserResult<AskGetabstractAnswer> {
  return ((
    value: unknown,
  ): ParserResult<{
    answer: string;
    contextDocs: AskGetabstractAnswerContextDoc[];
    detectedLanguageLabel: string | null;
    previousQuestionUuid: string | null;
    question: string;
    questionUuid: string;
    relatedActionables: ActionableMiniView[];
    relatedQuestions: string[];
    status: AskGetabstractAnswerStatus;
  }> => {
    if (typeof value !== "object") {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer]: '${value}' expected to be of type 'object'.` };
    }
    if (value === null) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer]: '${value}' expected to not be null.` };
    }
    if (!hasProp(value, "answer")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/answer]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'answer'.` };
    }
    const parsed_answer = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/answer]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["answer"]);
    })();
    if (!parsed_answer.success) {
      return { success: false, reason: parsed_answer.reason };
    }
    if (!hasProp(value, "contextDocs")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/contextDocs]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contextDocs'.` };
    }
    const parsed_contextDocs = ((): ParserResult<AskGetabstractAnswerContextDoc[]> => {
      return ((value: unknown): ParserResult<AskGetabstractAnswerContextDoc[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/contextDocs]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<AskGetabstractAnswerContextDoc> => {
          return deserialize_AskGetabstractAnswerContextDoc_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/AskGetabstractAnswer/contextDocs]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["contextDocs"]);
    })();
    if (!parsed_contextDocs.success) {
      return { success: false, reason: parsed_contextDocs.reason };
    }
    if (!hasProp(value, "detectedLanguageLabel")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/detectedLanguageLabel]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'detectedLanguageLabel'.` };
    }
    const parsed_detectedLanguageLabel = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/detectedLanguageLabel]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["detectedLanguageLabel"]);
    })();
    if (!parsed_detectedLanguageLabel.success) {
      return { success: false, reason: parsed_detectedLanguageLabel.reason };
    }
    if (!hasProp(value, "previousQuestionUuid")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/previousQuestionUuid]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'previousQuestionUuid'.` };
    }
    const parsed_previousQuestionUuid = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/previousQuestionUuid]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["previousQuestionUuid"]);
    })();
    if (!parsed_previousQuestionUuid.success) {
      return { success: false, reason: parsed_previousQuestionUuid.reason };
    }
    if (!hasProp(value, "question")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/question]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'question'.` };
    }
    const parsed_question = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/question]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["question"]);
    })();
    if (!parsed_question.success) {
      return { success: false, reason: parsed_question.reason };
    }
    if (!hasProp(value, "questionUuid")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/questionUuid]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'questionUuid'.` };
    }
    const parsed_questionUuid = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/questionUuid]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["questionUuid"]);
    })();
    if (!parsed_questionUuid.success) {
      return { success: false, reason: parsed_questionUuid.reason };
    }
    if (!hasProp(value, "relatedActionables")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/relatedActionables]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'relatedActionables'.` };
    }
    const parsed_relatedActionables = ((): ParserResult<ActionableMiniView[]> => {
      return ((value: unknown): ParserResult<ActionableMiniView[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/relatedActionables]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<ActionableMiniView> => {
          return deserialize_ActionableMiniView_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/AskGetabstractAnswer/relatedActionables]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["relatedActionables"]);
    })();
    if (!parsed_relatedActionables.success) {
      return { success: false, reason: parsed_relatedActionables.reason };
    }
    if (!hasProp(value, "relatedQuestions")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/relatedQuestions]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'relatedQuestions'.` };
    }
    const parsed_relatedQuestions = ((): ParserResult<string[]> => {
      return ((value: unknown): ParserResult<string[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/relatedQuestions]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<string> => {
          if (typeof value !== "string") {
            return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/relatedQuestions]: '${value}' expected to be of type string.` };
          }
          return { success: true, value: value };
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/AskGetabstractAnswer/relatedQuestions]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["relatedQuestions"]);
    })();
    if (!parsed_relatedQuestions.success) {
      return { success: false, reason: parsed_relatedQuestions.reason };
    }
    if (!hasProp(value, "status")) {
      return { success: false, reason: `[#/components/schemas/AskGetabstractAnswer/status]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'status'.` };
    }
    const parsed_status = ((): ParserResult<AskGetabstractAnswerStatus> => {
      return ((value: unknown): ParserResult<AskGetabstractAnswerStatus> => {
        return deserialize_AskGetabstractAnswerStatus_internal(value);
      })(value["status"]);
    })();
    if (!parsed_status.success) {
      return { success: false, reason: parsed_status.reason };
    }
    return {
      success: true,
      value: {
        answer: parsed_answer.value,
        contextDocs: parsed_contextDocs.value,
        detectedLanguageLabel: parsed_detectedLanguageLabel.value,
        previousQuestionUuid: parsed_previousQuestionUuid.value,
        question: parsed_question.value,
        questionUuid: parsed_questionUuid.value,
        relatedActionables: parsed_relatedActionables.value,
        relatedQuestions: parsed_relatedQuestions.value,
        status: parsed_status.value,
      },
    };
  })(value);
}
export function deserialize_AskGetabstractAnswer(str: string): ParserResult<AskGetabstractAnswer> {
  return deserialize_AskGetabstractAnswer_internal(parseJsonWithNumbersAsBigint(str));
}
